import {
  fetchBaseQuery,
  type BaseQueryApi,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import type { RefreshTokenDto, TokenResponse } from '@tixbase/contracts';
import { env } from '@/lib/env';
import { signedOut } from '@/features/auth/authSlice';
import { tokenStorage, type StoredTokens } from '@/features/auth/tokenStorage';
import { axiosBaseQuery, hasProgressCallback, type AxiosArgs } from './axiosBaseQuery';

export interface ApiExtraOptions {
  // 'bearer' as default
  auth?: 'bearer' | 'none';
  // 'fetch' as default (axios for features not supported by fetch like file upload progress)
  transport?: 'fetch' | 'axios';
}


export type AppQueryArgs = string | FetchArgs | AxiosArgs;

export type AppBaseQuery = BaseQueryFn<
  AppQueryArgs,
  unknown,
  FetchBaseQueryError,
  ApiExtraOptions,
  FetchBaseQueryMeta | undefined
>;

function bearerHeader(): Record<string, string> {
  const tokens = tokenStorage.get();
  return tokens ? { authorization: `Bearer ${tokens.accessToken}` } : {};
}

function createTransport(prepareHeaders?: () => Record<string, string>): AppBaseQuery {
  const fetchTransport = fetchBaseQuery({
    baseUrl: env.VITE_API_URL,
    prepareHeaders: (headers) => {
      for (const [name, value] of Object.entries(prepareHeaders?.() ?? {})) headers.set(name, value);
      return headers;
    },
  });

  const axiosTransport = axiosBaseQuery({ prepareHeaders });

  return (args, api, extraOptions = {}) => {
    const useAxios = extraOptions.transport === 'axios' || hasProgressCallback(args);

    return useAxios
      ? axiosTransport(args, api, extraOptions)
      : fetchTransport(args, api, extraOptions);
  };
}

const publicQuery = createTransport();
const authorizedQuery = createTransport(bearerHeader);

const refreshMutex = new Mutex();

export const baseQueryWithReauth: AppBaseQuery = async (args, api, extraOptions = {}) => {
  if (extraOptions.auth === 'none')
    return publicQuery(args, api, extraOptions);

  await refreshMutex.waitForUnlock();

  const tokens = tokenStorage.get();
  const result = await authorizedQuery(args, api, extraOptions);

  if (result.error?.status !== 401)
    return result;

  if (!tokens) {
    api.dispatch(signedOut());
    return result;
  }

  const refreshed = await refreshSession(api, tokens);

  if (!refreshed)
    return result;

  const retried = await authorizedQuery(args, api, extraOptions);

  if (retried.error?.status === 401)
    endSession(api);

  return retried;
};

const REFRESH_LOCK = 'tixbase.auth.refresh';

async function refreshSession(api: BaseQueryApi, tokens: StoredTokens): Promise<boolean> {
  if (refreshMutex.isLocked()) {
    await refreshMutex.waitForUnlock();

    const current = tokenStorage.get();
    return current !== null && current.accessToken !== tokens.accessToken;
  }

  const release = await refreshMutex.acquire();

  try {
    return await withCrossTabLock(() => refreshTokens(api, tokens));
  } finally {
    release();
  }
}

async function refreshTokens(api: BaseQueryApi, usedTokens: StoredTokens): Promise<boolean> {
  const tokens = tokenStorage.get();

  if (!tokens) {
    api.dispatch(signedOut());
    return false;
  }

  if (tokens.accessToken !== usedTokens.accessToken)
    return true;

  const body: RefreshTokenDto = { refreshToken: tokens.refreshToken };
  const result = await publicQuery({ url: 'auth/refresh', method: 'POST', body }, api, {});

  if (result.data) {
    const { accessToken, refreshToken } = result.data as TokenResponse;
    tokenStorage.set({ accessToken, refreshToken });

    return true;
  }

  if (result.error?.status === 401) {
    endSession(api);
    return false;
  }

  return false;
}

function endSession(api: BaseQueryApi): void {
  tokenStorage.clear();
  api.dispatch(signedOut());
}

async function withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return (await navigator.locks.request(REFRESH_LOCK, fn)) as T;
  }
  
  return await fn();
}
